import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  trackVehicleUploadStarted,
  trackVehicleUploadCompleted,
  trackVehicleUploadFailed,
} from "@/lib/posthog-client";

interface UploadResult {
  key: string;
  uploadUrl: string;
  publicUrl: string | null;
  vehicleUploadId?: string; // Returned for vehicle uploads
}

interface UploadResponse {
  publicUrl: string | null;
  vehicleUploadId?: string;
}

const MAX_UPLOAD_ATTEMPTS = 3;
const UPLOAD_RETRY_DELAY_MS = 400;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function useUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const upload = async (
    file: File,
    folder: "vehicles" | "rims" | "user-rims"
  ): Promise<UploadResponse | null> => {
    setIsUploading(true);
    setError(null);
    const uploadStartTime = Date.now();

    // Track vehicle upload started for funnel analysis
    if (folder === "vehicles") {
      trackVehicleUploadStarted({
        source: "file_picker", // Default to file_picker, could be enhanced with drag_drop detection
        fileType: file.type,
        fileSize: file.size,
      });
    }

    try {
      // Get presigned URL
      const presignedResponse = await fetch("/api/upload", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          folder,
          contentType: file.type,
          filename: file.name,
        }),
      });

      if (!presignedResponse.ok) {
        throw new Error("Failed to get upload URL");
      }

      const { uploadUrl, publicUrl, vehicleUploadId, key }: UploadResult = await presignedResponse.json();
      let finalizedVehicleUploadId = vehicleUploadId;

      // Upload file directly to R2 with retries for transient network/storage errors.
      let uploadResponse: Response | null = null;
      for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt++) {
        try {
          uploadResponse = await fetch(uploadUrl, {
            method: "PUT",
            body: file,
            headers: {
              "Content-Type": file.type,
            },
          });

          if (uploadResponse.ok) break;
        } catch (uploadError) {
          if (attempt === MAX_UPLOAD_ATTEMPTS) throw uploadError;
        }

        if (attempt < MAX_UPLOAD_ATTEMPTS) {
          await sleep(UPLOAD_RETRY_DELAY_MS * attempt);
        }
      }

      if (!uploadResponse?.ok) {
        const statusMessage = uploadResponse
          ? ` (status ${uploadResponse.status})`
          : "";
        throw new Error(`Failed to upload file${statusMessage}`);
      }

      // For vehicle uploads, verify the file exists in R2 before confirming
      if (folder === "vehicles" && key) {
        try {
          const verifyResponse = await fetch("/api/upload/verify", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              vehicleUploadId: finalizedVehicleUploadId,
              imageKey: key,
              originalFilename: file.name,
              contentType: file.type,
              fileSize: file.size,
            }),
          });

          if (!verifyResponse.ok) {
            const verifyData = await verifyResponse.json();
            // If verification fails (file doesn't exist), the record was already cleaned up
            throw new Error(verifyData.error || "Upload verification failed");
          }

          const verifyData = await verifyResponse.json();
          if (!verifyData.verified) {
            throw new Error("Upload verification failed - file not found");
          }

          finalizedVehicleUploadId = verifyData.vehicleUploadId || finalizedVehicleUploadId;

          if (!finalizedVehicleUploadId) {
            throw new Error("Upload verification failed - missing upload id");
          }
        } catch (verifyError) {
          // Verification failed - the record may have been cleaned up
          console.error("Upload verification failed:", verifyError);
          throw new Error(
            verifyError instanceof Error
              ? verifyError.message
              : "Upload verification failed"
          );
        }
      }

      // Invalidate vehicle uploads cache if this was a vehicle upload
      if (folder === "vehicles") {
        queryClient.invalidateQueries({ queryKey: ["vehicle-uploads"] });
        
        // Track vehicle upload completed for funnel analysis
        trackVehicleUploadCompleted({
          vehicleUploadId: finalizedVehicleUploadId || undefined,
          fileType: file.type,
          fileSize: file.size,
          uploadDurationMs: Date.now() - uploadStartTime,
        });
      }

      return { publicUrl, vehicleUploadId: finalizedVehicleUploadId };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed";
      setError(message);
      
      // Track vehicle upload failed for funnel analysis
      if (folder === "vehicles") {
        trackVehicleUploadFailed({
          error: message,
          fileType: file.type,
          fileSize: file.size,
        });
      }
      
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  return { upload, isUploading, error };
}
